import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePortfolio } from '../../context/PortfolioContext';
import GlassContainer from '../UI/GlassContainer';
import { 
  Code2, Star, ExternalLink, Github, Calendar, 
  UserCheck, Linkedin, Mail, MapPin, FileText, 
  CheckCircle2, Layers, Cpu, Database, Server, Layout, ArrowUpRight,
  Lock, Unlock, Sparkles, Globe 
} from 'lucide-react';

const DashboardView = ({ onSectionChange }) => {
  const { projects } = usePortfolio();
  const [isResumeHovered, setIsResumeHovered] = useState(false);

  // All projects with live demo URLs
  const liveProjects = (projects || []).filter(p => p.live_demo_url);

  // Curated, authentic skills - only what matters for 2026 job search
  const curatedSkills = [
    {
      category: 'Languages',
      icon: Code2,
      color: 'text-white',
      borderColor: 'border-white/20',
      items: ['TypeScript', 'JavaScript (ES6+)', 'Python', 'Java', 'SQL', 'HTML5 / CSS3'],
    },
    {
      category: 'Frontend & UI',
      icon: Layout,
      color: 'text-zinc-200',
      borderColor: 'border-white/15',
      items: ['React.js', 'Next.js (App Router)', 'Tailwind CSS', 'Redux / Context', 'Framer Motion'],
    },
    {
      category: 'Backend & APIs',
      icon: Server,
      color: 'text-zinc-300',
      borderColor: 'border-white/15',
      items: ['Node.js', 'Express.js', 'FastAPI', 'RESTful APIs', 'JWT Auth'],
    },
    {
      category: 'Databases & DevOps',
      icon: Database,
      color: 'text-zinc-400',
      borderColor: 'border-white/15',
      items: ['PostgreSQL', 'MongoDB', 'Docker', 'Git / GitHub', 'Sanity CMS', 'Vercel'],
    },
  ];

  // Top 3 featured projects with Umang Store #1
  const featured = (projects || [])
    .filter(p => p.featured)
    .slice(0, 3);

  return (
    <div className="w-full max-w-full space-y-5 pb-6">
      {/* 1. HERO & AVAILABILITY BANNER - PROMINENT TORONTO FOCUS */}
      <GlassContainer variant="card" className="p-4 sm:p-6 md:p-7 relative overflow-hidden border border-white/15">
        <div className="absolute top-0 right-0 w-48 sm:w-72 h-48 sm:h-72 bg-white/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5 relative z-10">
          <div className="space-y-2.5 flex-1 max-w-2xl">
            {/* Live Availability Tag */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-mono font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <span>Available to Work in Toronto, ON</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
              Shubh Soni
            </h1>

            <div className="text-zinc-300 font-mono text-sm sm:text-base md:text-lg font-medium">
              Full Stack Software Engineer
            </div>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-row md:flex-col gap-2.5 w-full md:w-auto shrink-0">
            <motion.button
              onClick={() => onSectionChange('resume')}
              onMouseEnter={() => setIsResumeHovered(true)}
              onMouseLeave={() => setIsResumeHovered(false)}
              whileHover={{ scale: 1.06, y: -2 }}
              whileTap={{ scale: 0.94 }}
              transition={{ type: "spring", stiffness: 450, damping: 17 }}
              className="relative group flex-1 md:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-mono font-medium overflow-hidden transition-all duration-300 bg-white text-black shadow-[0_4px_16px_rgba(255,255,255,0.25)] hover:shadow-[0_10px_30px_rgba(255,255,255,0.45)] border border-white"
            >
              {/* Dynamic light sweep shimmer beam */}
              <motion.div
                className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 pointer-events-none"
                animate={isResumeHovered ? { left: ['-100%', '200%'] } : { left: '-100%' }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
              />

              {/* Padlock Icon: Morphing with bounce from Lock to Unlock */}
              <motion.div
                animate={isResumeHovered ? { rotate: [0, -20, 14, 0], scale: [1, 1.25, 1.1] } : { rotate: 0, scale: 1 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="relative z-10 flex items-center text-black"
              >
                {isResumeHovered ? (
                  <Unlock className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Lock className="w-4 h-4 text-zinc-700" />
                )}
              </motion.div>

              {/* Dynamic label: Pops to 'Unlock Resume' */}
              <span className="relative z-10 font-bold tracking-tight">
                {isResumeHovered ? 'Unlock Resume' : 'Resume'}
              </span>

              {/* Animated sparkle badge when unlocked */}
              <AnimatePresence>
                {isResumeHovered && (
                  <motion.span
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative z-10 text-emerald-600 flex items-center"
                  >
                    <Sparkles className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '3s' }} />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            <a
              href="mailto:sonishubh2004@gmail.com"
              className="flex-1 md:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 hover:bg-white/20 text-white rounded-xl text-xs sm:text-sm font-mono font-medium transition-all"
            >
              <Mail className="w-4 h-4" />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </GlassContainer>

      {/* 2. QUICK RECRUITER METRICS BANNER */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <GlassContainer variant="card" className="p-3 sm:p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs text-terminal-dim">Location</div>
            <div className="text-sm sm:text-base font-semibold text-terminal-text truncate">Toronto, ON</div>
          </div>
          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-terminal-blue shrink-0 ml-2" />
        </GlassContainer>

        <GlassContainer variant="card" className="p-3 sm:p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs text-terminal-dim">Work Status</div>
            <div className="text-sm sm:text-base font-semibold text-terminal-green truncate">Ready to Work</div>
          </div>
          <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-terminal-green shrink-0 ml-2" />
        </GlassContainer>

        <GlassContainer variant="card" className="p-3 sm:p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs text-terminal-dim">Experience</div>
            <div className="text-sm sm:text-base font-semibold text-terminal-text truncate">5+ Years Coding</div>
          </div>
          <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-terminal-green shrink-0 ml-2" />
        </GlassContainer>

        <GlassContainer variant="card" className="p-3 sm:p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs text-terminal-dim">Direct Connect</div>
            <div className="flex items-center gap-2.5 mt-1">
              <a 
                href="https://linkedin.com/in/shubhsoni" 
                target="_blank" 
                rel="noreferrer" 
                className="text-terminal-dim hover:text-terminal-green transition-colors p-0.5" 
                title="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="https://github.com/shubhhh19" 
                target="_blank" 
                rel="noreferrer" 
                className="text-terminal-dim hover:text-terminal-green transition-colors p-0.5" 
                title="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a 
                href="mailto:sonishubh2004@gmail.com" 
                className="text-terminal-dim hover:text-terminal-green transition-colors p-0.5" 
                title="Email"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
          <span className="text-terminal-green text-xs font-mono shrink-0 font-bold ml-2">2026</span>
        </GlassContainer>
      </div>

      {/* 3. SKILLS SECTION - MOVED BEFORE PROJECTS (Curated & Authentic) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
            <Code2 className="w-5 h-5 text-white" />
            <span>Core Technical Stack</span>
          </h2>
        </div>

        {/* 4 Focused Category Cards - Responsive 1-col on mobile, 2-col on tablet, 4-col on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {curatedSkills.map((group) => {
            const IconComponent = group.icon;
            return (
              <GlassContainer 
                key={group.category} 
                variant="strong" 
                className={`p-3.5 sm:p-4 rounded-2xl border ${group.borderColor} space-y-2.5 flex flex-col justify-between`}
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <IconComponent className={`w-4 h-4 ${group.color}`} />
                    <h3 className="font-semibold text-xs sm:text-sm text-white font-mono">
                      {group.category}
                    </h3>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {group.items.map((skill) => (
                      <span
                        key={skill}
                        className="px-2 py-1 text-[11px] rounded-lg bg-white/5 text-zinc-300 border border-white/10 font-mono"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </GlassContainer>
            );
          })}
        </div>
      </div>

      {/* 4. FEATURED PROJECTS - UMANG STORE PROMINENTLY #1 */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-zinc-300 fill-current" />
            <span>Featured Projects</span>
          </h2>
          <button
            onClick={() => onSectionChange('projects')}
            className="text-zinc-400 hover:text-white transition-colors font-mono text-xs flex items-center gap-1"
          >
            <span>View All Projects</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Responsive Grid: 1 col on mobile, 2 on tablet, 3 on large screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {featured.map((project) => (
            <GlassContainer 
              key={project.id}
              variant="card" 
              className="p-4 sm:p-5 flex flex-col justify-between hover:shadow-[0_8px_30px_rgba(255,255,255,0.08)] transition-all duration-300 border border-white/10 hover:border-white/30 group rounded-2xl"
            >
              <div className="space-y-2.5">
                {/* Title & Tag */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-white group-hover:text-zinc-200 text-sm sm:text-base transition-colors leading-snug">
                    {project.title}
                  </h3>
                  {project.id === '11' && (
                    <span className="shrink-0 px-2 py-0.5 text-[10px] font-mono font-medium bg-white/10 text-white border border-white/30 rounded-full">
                      New • Live
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-xs sm:text-sm text-zinc-400 line-clamp-3 leading-relaxed">
                  {project.description}
                </p>

                {/* Tech Stack Pills */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {project.tech_stack && project.tech_stack.slice(0, 4).map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 text-[10px] sm:text-[11px] bg-white/5 text-zinc-300 border border-white/10 rounded font-mono"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/10 text-xs font-mono">
                <div className="flex items-center gap-2.5">
                  {project.live_demo_url && (
                    <a
                      href={project.live_demo_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-white hover:text-zinc-300 transition-colors font-medium"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>Live Demo</span>
                    </a>
                  )}
                  {project.github_url && (
                    <a
                      href={project.github_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-zinc-400 hover:text-white transition-colors"
                    >
                      <Github className="w-3.5 h-3.5" />
                      <span>Code</span>
                    </a>
                  )}
                </div>

                <button
                  onClick={() => onSectionChange('projects')}
                  className="text-zinc-400 hover:text-white transition-colors text-[11px]"
                >
                  Details →
                </button>
              </div>
            </GlassContainer>
          ))}
        </div>
      </div>

      {/* 5. TORONTO HIRING & VALUE OVERVIEW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 pt-2">
        <GlassContainer variant="card" className="p-4 sm:p-5 space-y-3 rounded-2xl border border-white/10">
          <div className="flex items-center justify-between border-b border-white/10 pb-2.5">
            <h3 className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-white" />
              <span>Target Roles (Toronto & Remote)</span>
            </h3>
            <span className="text-[11px] font-mono text-zinc-400">2026 Search</span>
          </div>

          <div className="space-y-2 text-xs sm:text-sm text-zinc-200">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-white shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Full Stack Software Engineer:</strong>
                <span className="text-zinc-400 ml-1">React, Next.js, Node.js, Express, TypeScript.</span>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-white shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Backend & APIs:</strong>
                <span className="text-zinc-400 ml-1">Python, FastAPI, PostgreSQL, MongoDB, RESTful endpoints.</span>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-white shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Location Readiness:</strong>
                <span className="text-zinc-400 ml-1">Available for in-person, hybrid, or remote work across the Greater Toronto Area (GTA).</span>
              </div>
            </div>
          </div>
        </GlassContainer>

        <GlassContainer variant="card" className="p-4 sm:p-5 space-y-3 rounded-2xl border border-white/10 flex flex-col">
          <div className="flex items-center justify-between border-b border-white/10 pb-2.5">
            <h3 className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
              <Globe className="w-4 h-4 text-white" />
              <span>Live Projects</span>
            </h3>
            <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>{liveProjects.length} Deployed</span>
            </span>
          </div>

          <div className="space-y-2 text-xs sm:text-sm text-zinc-200 max-h-72 overflow-y-auto pr-1 no-scrollbar">
            {liveProjects.map((p) => (
              <a
                key={p.id}
                href={p.live_demo_url}
                target="_blank"
                rel="noreferrer"
                className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-between gap-2 transition-all group"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-white group-hover:text-zinc-200 truncate flex items-center gap-1.5">
                    <span>{p.title}</span>
                    {p.id === '11' && (
                      <span className="px-1.5 py-0.5 text-[9px] font-mono bg-white/15 text-white border border-white/30 rounded font-semibold">
                        Umang Store
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 truncate mt-0.5">
                    {p.tech_stack ? p.tech_stack.slice(0, 3).join(' • ') : p.description}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-zinc-400 group-hover:text-white shrink-0 font-mono text-[11px] pl-2">
                  <span>Visit</span>
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
              </a>
            ))}
          </div>
        </GlassContainer>
      </div>

      {/* 6. COMPACT TERMINAL SHORTCUTS FOOTER */}
      <GlassContainer variant="card" className="p-3 text-center rounded-xl">
        <div className="font-mono text-[11px] sm:text-xs text-zinc-400 flex flex-wrap items-center justify-center gap-2">
          <span>⚡ Terminal Quick Access:</span>
          <span>Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white border border-white/20">⌘T</kbd></span>
          <span>• Commands: <code className="text-white">projects</code>, <code className="text-white">resume</code>, <code className="text-white">contact</code></span>
        </div>
      </GlassContainer>
    </div>
  );
};

export default DashboardView;